// All fetch calls go through /api — proxied to the Worker in dev,
// and hits the deployed Worker URL in production.

export interface StockData {
  ticker: string;
  name: string;
  sector: string;
  marketCap: number;
  description: string;
  price: number;
  open: number;
  high: number;
  low: number;
  volume: number;
  vwap: number;
  changePercent: number;
  prevClose: number;
  iv: number;
  ivRank: number;
  hvRatio: number;
  historicalVol: number;
}

export interface OptionLeg {
  strike: number;
  type: "call" | "put";
  premium: string;
  premiumTotal: string;
  annualYield: string;
  delta: string;
  gamma: string;
  theta: string;
  vega: string;
  probProfit: string;
  dte: number;
  breakeven: string;
  maxProfit: string;
  maxLoss: string;
  impliedMove: string;
}

export interface OptionsChain {
  ticker: string;
  price: number;
  iv: number;
  ivRank: number;
  hvRatio: number;
  strategy: string;
  dte: number;
  chain: OptionLeg[];
}

const BASE = "/api";

// All fetch calls include credentials for auth cookies
const fetchWithAuth = (url: string, init?: RequestInit): Promise<Response> =>
  fetch(url, { ...init, credentials: 'include' });

export async function fetchStocks(): Promise<StockData[]> {
  const res = await fetchWithAuth(`${BASE}/stocks`);
  if (!res.ok) throw new Error(`Failed to fetch stocks: ${res.status}`);
  return res.json();
}

export async function fetchOptions(
  ticker: string,
  strategy: "sell_put" | "sell_call",
  dte: number
): Promise<OptionsChain> {
  const res = await fetchWithAuth(`${BASE}/options?ticker=${ticker}&strategy=${strategy}&dte=${dte}`);
  if (!res.ok) throw new Error(`Failed to fetch options: ${res.status}`);
  return res.json();
}

export async function analyzeOption(payload: {
  ticker: string;
  stockName: string;
  price: number;
  sector: string;
  iv: number;
  ivRank: number;
  hvRatio: number;
  strategy: string;
  option: OptionLeg & { strike: number };
}): Promise<string> {
  const res = await fetchWithAuth(`${BASE}/analyze`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Analysis failed: ${res.status}`);
  const data = await res.json() as { analysis: string };
  return data.analysis;
}

// ─── New interfaces ────────────────────────────────────────────────────────

export type Strategy = 'bull_put_spread' | 'bear_call_spread' | 'sell_put' | 'sell_call' | 'buy_call' | 'buy_put';
export type TradeStatus = 'open' | 'rolled' | 'closed_profit' | 'closed_loss' | 'expired_worthless' | 'assigned';
export type MonitorSignal = 'hold' | 'roll' | 'exit_profit' | 'exit_loss' | 'approaching_expiry';

export interface Recommendation {
  id: number;
  run_id: number;
  rank: number;
  ticker: string;
  strategy: Strategy;
  long_strike: number | null;
  long_expiry: string | null;
  long_premium: number | null;
  short_strike: number;
  short_expiry: string;
  short_premium: number;
  short_delta: number | null;
  short_theta: number | null;
  dte: number;
  spot_price: number;
  iv_rank: number;
  iv_current: number;
  hv30: number | null;
  net_premium: number;
  max_profit: number;
  max_loss: number;
  breakeven: number;
  risk_reward: number;
  pop: number;
  expected_value: number;
  ah_change: number | null;
  score_total: number;
  score_sentiment: number | null;
  sentiment_bullish: number | null;
  sentiment_bearish: number | null;
  sentiment_buzz: number | null;
  macro_risk_level: number | null;
  vix_level: number | null;
  earnings_within_dte: number | null;
  sentiment_label: string | null;
  created_at: string;
}

export interface ScreenResult {
  run_id: number;
  run_at: string;
  recommendations: Recommendation[];
}

export interface Trade {
  id: number;
  rec_id: number | null;
  ticker: string;
  strategy: Strategy;
  long_strike: number | null;
  long_expiry: string | null;
  long_fill: number | null;
  short_strike: number;
  short_expiry: string;
  short_fill: number;
  quantity: number;
  multiplier: number;
  net_credit: number;
  max_profit: number;
  max_loss: number;
  opened_at: string;
  opened_spot: number;
  status: TradeStatus;
  closed_at: string | null;
  close_debit: number | null;
  realized_pnl: number | null;
  notes: string | null;
  is_agent_recommended: number;
  // joined from monitoring
  latest_signal?: MonitorSignal;
  latest_signal_reason?: string;
  unrealized_pnl?: number;
  pnl_pct?: number;
  days_remaining?: number;
}

export interface PortfolioGreeks {
  snapped_at: string;
  open_trades: number;
  total_delta: number;
  total_theta: number;
  total_max_loss: number;
  total_unrealized_pnl?: number;
}

// ─── New API functions ─────────────────────────────────────────────────────

export async function fetchScreenLatest(): Promise<ScreenResult> {
  const res = await fetchWithAuth(`${BASE}/screen/latest`);
  if (!res.ok) throw new Error(`Screen fetch failed: ${res.status}`);
  return res.json();
}

export async function triggerScreen(): Promise<{ message: string }> {
  const res = await fetchWithAuth(`${BASE}/screen/trigger`);
  if (!res.ok) throw new Error(`Trigger failed: ${res.status}`);
  return res.json();
}

export async function fetchTrades(status: 'open' | 'closed' | 'all' = 'open'): Promise<Trade[]> {
  const res = await fetchWithAuth(`${BASE}/trades?status=${status}`);
  if (!res.ok) throw new Error(`Trades fetch failed: ${res.status}`);
  const data = await res.json() as { trades: Trade[] };
  return data.trades;
}

export async function openTrade(payload: {
  rec_id?: number;
  ticker: string;
  strategy: Strategy;
  short_strike: number;
  short_expiry: string;
  short_fill: number;
  long_strike?: number;
  long_expiry?: string;
  long_fill?: number;
  quantity: number;
  opened_spot: number;
  notes?: string;
}): Promise<Trade> {
  const res = await fetchWithAuth(`${BASE}/trades`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Open trade failed: ${res.status}`);
  const data = await res.json() as { trade: Trade };
  return data.trade;
}

export async function closeTrade(id: number, payload: {
  action: 'close' | 'expire';
  closed_spot?: number;
  close_debit?: number;
  notes?: string;
}): Promise<Trade> {
  const res = await fetchWithAuth(`${BASE}/trades/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Close trade failed: ${res.status}`);
  const data = await res.json() as { trade: Trade };
  return data.trade;
}

export async function fetchPortfolio(): Promise<PortfolioGreeks> {
  const res = await fetchWithAuth(`${BASE}/portfolio`);
  if (!res.ok) throw new Error(`Portfolio fetch failed: ${res.status}`);
  return res.json();
}

// ─── Stock Picks types & API ──────────────────────────────────────────────

export type StockPickCategory = 'growth' | 'long_term' | 'value' | 'super_long_term';

export interface StockPick {
  id: number;
  run_id: number;
  category: StockPickCategory;
  asset_type: 'etf' | 'stock' | null;
  rank: number;
  ticker: string;
  name: string | null;
  sector: string | null;
  market_cap: number;
  price: number;
  change_1m: number | null;
  change_3m: number | null;
  change_6m: number | null;
  change_1y: number | null;
  hv30: number | null;
  iv_current: number | null;
  iv_rank: number | null;
  iv_hv_ratio: number | null;
  sma50_pct: number | null;
  sma200_pct: number | null;
  volume_avg: number | null;
  beta_proxy: number | null;
  score_momentum: number;
  score_volatility: number;
  score_fundamental: number;
  score_technical: number;
  score_sentiment: number;
  score_value: number;
  score_total: number;
  sentiment_label: string | null;
  macro_risk_level: number | null;
  vix_level: number | null;
  target_price_agent: number | null;
  target_price_market: number | null;
  target_upside_agent: number | null;
  target_upside_market: number | null;
  pe_ratio: number | null;
  pb_ratio: number | null;
  eps_growth: number | null;
  revenue_growth: number | null;
  roe: number | null;
  debt_equity: number | null;
  net_margin: number | null;
  free_cash_flow_ps: number | null;
  dividend_yield: number | null;
  signal: string;
  signal_reason: string | null;
  created_at: string;
}

export interface StockPicksResult {
  run_id: number | null;
  run_at: string | null;
  growth: StockPick[];
  long_term: StockPick[];
  value: StockPick[];
  super_long_term: StockPick[];
}

export interface StockHolding {
  id: number;
  pick_id: number | null;
  ticker: string;
  category: string;
  buy_price: number;
  quantity: number;
  total_cost: number;
  bought_at: string;
  status: string;
  sell_price: number | null;
  sold_at: string | null;
  realized_pnl: number | null;
  realized_pnl_pct: number | null;
  notes: string | null;
  current_price: number | null;
  unrealized_pnl: number | null;
  unrealized_pnl_pct: number | null;
  signal: string;
  signal_reason: string | null;
  last_monitored: string | null;
  is_agent_pick: number;
}

export async function fetchStockPicks(): Promise<StockPicksResult> {
  const res = await fetchWithAuth(`${BASE}/stock-picks/latest`);
  if (!res.ok) throw new Error(`Stock picks fetch failed: ${res.status}`);
  return res.json();
}

export async function triggerStockPicks(): Promise<{ message: string }> {
  const res = await fetchWithAuth(`${BASE}/stock-picks/trigger`);
  if (!res.ok) throw new Error(`Stock picks trigger failed: ${res.status}`);
  return res.json();
}

export async function fetchStockPortfolio(status: 'holding' | 'sold' | 'all' = 'holding'): Promise<StockHolding[]> {
  const res = await fetchWithAuth(`${BASE}/stock-picks/portfolio?status=${status}`);
  if (!res.ok) throw new Error(`Portfolio fetch failed: ${res.status}`);
  const data = await res.json() as { holdings: StockHolding[] };
  return data.holdings;
}

export async function buyStock(payload: {
  pick_id?: number;
  ticker: string;
  category: string;
  buy_price: number;
  quantity: number;
  notes?: string;
  is_agent_pick?: boolean;
}): Promise<StockHolding> {
  const res = await fetchWithAuth(`${BASE}/stock-picks/portfolio`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Buy stock failed: ${res.status}`);
  return res.json();
}

export async function sellStockHolding(id: number, payload: {
  sell_price: number;
  quantity?: number;
  notes?: string;
}): Promise<StockHolding> {
  const res = await fetchWithAuth(`${BASE}/stock-picks/portfolio/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Sell stock failed: ${res.status}`);
  return res.json();
}

export async function updateStockHolding(id: number, payload: {
  buy_price?: number;
  quantity?: number;
  category?: string;
  notes?: string;
  is_agent_pick?: boolean;
}): Promise<StockHolding> {
  const res = await fetchWithAuth(`${BASE}/stock-picks/portfolio/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Update holding failed: ${res.status}`);
  return res.json();
}

export async function deleteStockHolding(id: number): Promise<void> {
  const res = await fetchWithAuth(`${BASE}/stock-picks/portfolio/${id}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(`Delete holding failed: ${res.status}`);
}
